<template>
    <div class="cover-outer-container" :class="{ 'is-pdf': isPdf }">
        <div v-if="expandCover === false && !isPdf" class="expand-cover-button-container">
            <oButton
                :size="aboveMobile ? 'extra-large' : 'medium'"
                color="blue"
                @clicked="expandCover = true"
            >
                Show Expanded Cover
            </oButton>
        </div>
        <div v-if="expandCover === true && !isPdf" class="hide-cover-button-container">
            <oButton size="extra-large" color="blue" @clicked="expandCover = false">
                Hide Expanded Cover
            </oButton>
        </div>

        <div class="cover-content-container">
            <!-- Cover heading -->
            <div class="cover-heading">
                <Text as="h1" align="center" weight="600">Google Ads Scorecard</Text>
                <Skeleton
                    v-if="isLoading"
                    :width="300"
                    :height="14"
                    class="created-for-skeleton"
                    style="margin-top: 0.375rem; margin-bottom: 0.25rem"
                />
                <div v-else>
                    <Text class="created-for" as="p" size="f-7" align="center" color="gray">
                        Created for {{ coverAccountName }} on {{ formattedDate }}
                    </Text>
                    <Text class="created-for-mobile" as="p" size="f-9" align="center" color="gray">
                        Generated {{ formattedDate }}
                    </Text>
                </div>
            </div>

            <!-- Cover content -->
            <Tooltip
                :content="mode === 'app' ? 'Click to Edit' : ''"
                followCursor
                theme="click-to-edit"
                :offset="[0, 4]"
                :duration="300"
                :zIndex="10000"
                style="max-width: 100%"
            >
                <div
                    :class="[mode === 'app' ? 'pointer' : '', 'cover-content']"
                    @click="mode === 'live' ? null : $emit('clicked-account-name')"
                >
                    <div :class="['avatar-container']">
                        <Skeleton
                            v-if="isLoading"
                            :width="60"
                            :height="60"
                            class="scorecard-cover-avatar"
                        />
                        <Skeleton
                            v-if="isLoading"
                            :width="186"
                            :height="36"
                            class="scorecard-cover-domain-name-skeleton"
                        />
                        <div v-else-if="!accountLogoUrl" class="avatar-no-image">
                            <Avatar
                                :color="domainColour"
                                :width="60"
                                :height="60"
                                :fontSize="18"
                                class="scorecard-cover-avatar"
                                :show-account-type="false"
                                account-type-size="large"
                            >
                                {{ accountInitials }}
                            </Avatar>
                            <Text
                                as="h1"
                                weight="600"
                                class="scorecard-cover-domain-name"
                                :style="{ color: domainColour, letterSpacing: '-0.075rem' }"
                            >
                                {{ coverAccountName }}
                            </Text>
                        </div>
                        <div class="relative" v-else>
                            <!-- go for 48 x 180 -->
                            <!-- *1.25 = 225 x 60 -->
                            <!--
                                standard img element with max-width and max-height gives
                                more flexibility here and prevents the container from
                                having unnecessary vertical/horizontal space, is there
                                any reason we can't do this, i.e. PDF compatibility?
                            -->
                            <img :src="accountLogoUrl" class="avatar-image" />
                        </div>
                    </div>
                    <div
                        v-if="
                            mode === 'app' &&
                            !isLoading &&
                            !websiteUrl &&
                            industry === '' &&
                            location === ''
                        "
                        class="account-item-container"
                    >
                        <!-- WEBSITE -->
                        <div class="account-item website">
                            <WebsiteUrlIcon class="icon" style="opacity: 0.3" />
                            <Text as="p" size="f-8" color="gray">website.com</Text>
                        </div>
                        <!-- INDUSTRY -->
                        <div class="account-item industry">
                            <IndustryIcon class="icon" style="opacity: 0.3" />
                            <Text as="p" size="f-8" color="gray">Industry</Text>
                        </div>
                        <!-- TIMEZONE -->
                        <div class="account-item location">
                            <TimezoneIcon class="icon" style="opacity: 0.3" />
                            <Text as="p" size="f-8" color="gray">City, State</Text>
                        </div>
                    </div>
                    <div
                        v-else-if="!isLoading"
                        :class="[
                            {
                                'account-item-container':
                                    websiteUrl || industry !== '' || location !== '',
                            },
                            { 'container-focus': mode === 'app' },
                            { 'no-pointer': mode === 'live' },
                            { 'pdf-shadow-fix': isPdf },
                        ]"
                    >
                        <!-- WEBSITE -->
                        <div v-if="websiteUrl" class="account-item website">
                            <WebsiteUrlIcon class="icon" />
                            <component
                                :is="mode === 'live' ? 'a' : 'span'"
                                :href="
                                    websiteUrl?.includes('http')
                                        ? websiteUrl
                                        : `https://` + websiteUrl
                                "
                                target="_blank"
                            >
                                <Text as="p" size="f-8">{{ websiteUrl }}</Text>
                            </component>
                        </div>
                        <!-- INDUSTRY -->
                        <div v-if="industry !== ''" class="account-item industry">
                            <IndustryIcon class="icon" />
                            <Text as="p" size="f-8">{{ industry }}</Text>
                        </div>
                        <!-- TIMEZONE -->
                        <div v-if="location !== ''" class="account-item location">
                            <TimezoneIcon class="icon" />
                            <Text as="p" size="f-8">{{ location }}</Text>
                        </div>
                    </div>
                    <div v-else class="account-item-container">
                        <!-- WEBSITE -->
                        <div class="account-item website">
                            <WebsiteUrlIcon class="icon" />
                            <Skeleton :width="120" :height="14" />
                        </div>
                        <!-- INDUSTRY -->
                        <div class="account-item industry">
                            <IndustryIcon class="icon" />
                            <Skeleton :width="148" :height="14" />
                        </div>
                        <!-- TIMEZONE -->
                        <div class="account-item location">
                            <TimezoneIcon class="icon" />
                            <Skeleton :width="120" :height="14" />
                        </div>
                    </div>
                </div>
            </Tooltip>

            <!-- Cover footer -->
            <Tooltip
                :content="mode === 'app' ? 'Click to Edit' : ''"
                followCursor
                theme="click-to-edit"
                :offset="[0, 4]"
                :duration="300"
                :zIndex="10000"
                style="max-width: 100%"
            >
                <div
                    :class="['cover-footer', { 'no-pointer': mode === 'live' }]"
                    @click="mode === 'live' ? null : $emit('clicked-sender-name')"
                >
                    <SenderInfoLayout
                        :user-id="userId"
                        :user-email="userEmail"
                        :user-full-name="userFullName"
                        :profile-image-url="profileImageUrl"
                        :is-pdf="isPdf"
                        :is-auto-generated="isAutoGenerated"
                    />
                </div>
            </Tooltip>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, ref, defineComponent, PropType } from 'vue'
import formatDate from 'date-fns/format'

import {
    Text,
    Link,
    Spacer,
    Avatar,
    WebsiteUrlIcon,
    IndustryIcon,
    TimezoneIcon,
    ProfileImage,
    TableEdit,
    oButton,
    Tooltip,
    OpteoLogo,
} from '@opteo/components-next'

import useMediaQuery from '@/composition/global/useMediaQuery'
import Skeleton from '@/components/util/Skeleton.vue'
import SenderInfoLayout from './layouts/SenderInfoLayout.vue'
import { getAccountInitials } from './utils'

export default defineComponent({
    name: 'Cover',
    props: {
        websiteUrl: {
            type: [String, null] as PropType<string | null>,
        },
        industry: {
            type: [String, null] as PropType<string | null>,
        },
        accountLogoUrl: {
            type: [String, null] as PropType<string | null>,
        },
        domainName: {
            type: String,
        },
        accountName: {
            type: [String, null] as PropType<string | null>,
        },
        domainColour: {
            type: String,
        },
        location: {
            type: [String, null] as PropType<string | null>,
        },
        isLoading: {
            type: Boolean,
        },
        lastUpdated: {
            type: [String, Date] as PropType<string | Date>,
            required: true,
        },
        userFullName: {
            type: String,
        },
        userEmail: {
            type: String,
        },
        userId: {
            type: Number,
        },
        profileImageUrl: {
            type: [String, null] as PropType<string | null>,
        },
        mode: {
            type: String as PropType<'live' | 'pdf' | 'app'>,
            required: true,
        },
        isAutoGenerated: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    components: {
        Text,
        Spacer,
        Link,
        Avatar,
        Skeleton,
        WebsiteUrlIcon,
        IndustryIcon,
        TimezoneIcon,
        ProfileImage,
        TableEdit,
        oButton,
        Tooltip,
        OpteoLogo,
        SenderInfoLayout,
    },
    emits: ['clicked-account-name', 'clicked-sender-name'],
    setup(props) {
        const isPdf = props.mode === 'pdf'

        const coverAccountName = computed(() => props?.accountName || props?.domainName)
        const accountInitials = computed(() => {
            return getAccountInitials(coverAccountName.value)
        })

        const formattedDate = computed(() => {
            const date = new Date(props.lastUpdated)
            return formatDate(date, 'do LLLL yyyy')
        })

        const showAccountEditPencil = ref(false)
        const showInfoBarEditPencil = ref(false)
        const showSenderEditPencil = ref(false)

        const expandCover = ref(props.mode === 'pdf') // expand by default in pdf mode
        const { aboveMobile } = useMediaQuery()

        const coverHeight = computed(() => {
            if (aboveMobile.value && expandCover.value) {
                return '82.25rem'
            } else if (aboveMobile.value && !expandCover.value) {
                return '33rem'
            } else if (!aboveMobile.value && expandCover.value) {
                return '100%'
            } else return '13.875rem'
        })

        return {
            isPdf,
            showAccountEditPencil,
            showInfoBarEditPencil,
            showSenderEditPencil,
            accountInitials,
            formattedDate,
            expandCover,
            coverAccountName,
            coverHeight,
            aboveMobile,
        }
    },
})
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

.cover-outer-container {
    height: v-bind(coverHeight);
    overflow: hidden;
    break-before: page; // used for pdf
}

.cover-outer-container.is-pdf {
    height: 100%;
}

.expand-cover-button-container {
    @include absolute;
    bottom: 0;
    @include w-100;
    @include flex;
    @include justify-center;
    @include items-end;
    @include pb-24;
    height: 10rem;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%);
}
.hide-cover-button-container {
    @include absolute;
    bottom: 0;
    @include w-100;
    @include flex;
    @include justify-center;
    @include items-end;
    @include pb-24;
    height: 10rem;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%);
}

.cover-content {
    cursor: default;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    @include ph-40;
    max-width: 100%;
}
.cover-content.pointer {
    @include pointer;
}
.cover-content-container {
    @include flex;
    flex-direction: column;
    @include items-center;
    @include justify-between;
    padding: 8rem 0;
    height: 82.25rem;
}

.cover-heading {
    @include flex;
    flex-direction: column;
    @include items-center;
    gap: 0.75rem;
}

.scorecard-cover-domain-name {
    white-space: nowrap;
    max-width: 30rem;
    overflow: hidden;
    text-overflow: ellipsis;
    @include lh-130;
}

.avatar-container {
    @include flex-center;
    @include relative;
}

.avatar-no-image {
    @include flex-center;
}

.account-item-container {
    @include container;
    @include pv-20;
    @include ph-28;
    @include inline-flex;
    @include items-center;
    margin: 0 auto;
    @include relative;
    @include br-24;
    gap: 1.625rem;
    max-width: 100%;
}

.account-item {
    @include flex;
    @include items-center;
    height: 2rem;
    gap: 1rem;
}
.account-item p {
    white-space: nowrap;
}
.account-item.website {
    gap: 0.875rem;
}

.cover-footer {
    @include pointer;
    @include ph-24;
    @include relative;
}

.no-pointer {
    cursor: default;
}

.scorecard-cover-avatar {
    margin-right: 1.25rem;
}

// Edit Pencil transition
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.created-for-mobile {
    display: none;
}

.avatar-image {
    max-width: 300px;
    max-height: 200px;
}

// Responsive.
// PDF renders should never be responsive. For arcane reasons sometimes they trigger media queries despite being plenty wide, so we prevent that here with :not().
.cover-outer-container:not(.is-pdf) {
    @media (max-width: $mq-480-max) {
        .account-item-container {
            padding: 0.875rem 1.375rem;
            overflow: auto;
            gap: 1.25rem;
        }
        .account-item {
            @include mh-0;
            @include mv-4;
        }
        .account-item :deep(p) {
            @include f-9;
        }
        .cover-content-container {
            padding-top: 2.5rem;
            padding-bottom: 2.5rem;
            height: 40rem;
        }
        .cover-content-container :deep(h1) {
            font-size: 1.5rem;
            letter-spacing: -0.03rem !important;
        }
        .created-for {
            display: none;
        }
        .created-for-mobile {
            display: block;
        }
        .avatar-image {
            max-width: 12rem;
            max-height: 4.5rem;
        }
        .cover-heading {
            gap: 0.625rem;
        }
        .hide-cover-button-container {
            display: none;
        }
        .scorecard-cover-avatar {
            width: 36px !important;
            height: 36px !important;
            font-size: 11px !important;
            flex: 0 0 36px !important;
            margin-right: 0.875rem;
        }
        .scorecard-cover-domain-name-skeleton {
            height: 20px !important;
            width: 148px !important;
        }
        .created-for-skeleton {
            height: 11px !important;
            width: 220px !important;
        }
    }

    @media screen and (min-width: $mq-481-min) and (max-width: $mq-767-max) {
        .account-item-container {
            flex-direction: column;
            @include ph-32;
            @include pv-20;
        }
        .account-item {
            @include mh-0;
            @include mv-4;
        }
        .cover-content-container {
            padding-top: 3rem;
            padding-bottom: 3rem;
            height: 56rem;
        }
        .cover-content-container :deep(h1) {
            font-size: 1.8125rem;
            letter-spacing: -0.05rem !important;
        }
        .created-for {
            display: none;
        }
        .created-for-mobile {
            display: block;
        }
        .avatar-image {
            max-width: 240px;
            max-height: 96px;
        }
        .hide-cover-button-container {
            display: none;
        }
        .scorecard-cover-avatar {
            width: 40px !important;
            height: 40px !important;
            font-size: 11px !important;
            flex: 0 0 40px !important;
            margin-right: 0.875rem;
        }
        .scorecard-cover-domain-name-skeleton {
            height: 22px !important;
        }
        .created-for-skeleton {
            height: 11px !important;
            width: 220px !important;
        }
    }
    @media screen and (min-width: $mq-768-min) and (max-width: $mq-1024-max) {
        .account-item-container {
            flex-direction: column;
            @include ph-32;
            @include pv-20;
        }
        .account-item {
            @include mv-8;
            @include mh-0;
        }
        .cover-content-container :deep(h1) {
            font-size: 2rem;
        }
    }
}
</style>
